/** General-purpose utilities for parsing, formatting, and calculating durations and time intervals. */
// @ts-check
import { MS_PER_MINUTE } from '@/config/constants';

/**
 * Formats a Date object into a standardized date and time object.
 * @param {Date} dateObj - The Date object to format.
 * @returns {{ date: string, time: string }}
 */
const formatDateAndTime = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  };
};

/**
 * Returns the current date (YYYY-MM-DD) and time (HH:mm).
 * @returns {{ date: string, time: string }}
 */
export const getCurrentDateAndTime = () => formatDateAndTime(new Date());

/**
 * Parses a time string in "HH:mm" format to the total number of minutes from midnight.
 * @param {string} timeStr - The time string to parse (e.g., "09:30").
 * @returns {number} The total minutes from midnight. Returns 0 if input is invalid.
 */
export const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || !/^\d{2}:\d{2}$/.test(timeStr)) {
    return 0;
  }
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Formats a total number of minutes from midnight into an "HH:mm" time string.
 * @param {number} totalMinutes - The total minutes from midnight.
 * @returns {string} The formatted time string (e.g., "09:30").
 */
export const formatMinutesToTime = (totalMinutes) => {
  if (typeof totalMinutes !== 'number' || totalMinutes < 0) {
    return '00:00';
  }
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = Math.round(totalMinutes) % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Parses a duration input string, accepting plain minutes or an "h"/"u" hour
 * suffix (optionally combined with minutes), e.g. "90", "1.5u", "4h", "1u30".
 * @param {string|number} input - The raw input value.
 * @returns {number|null} The duration in minutes, or null if unparseable.
 */
export const parseDurationInput = (input) => {
  if (input === null || input === undefined) return null;
  const str = String(input).trim().toLowerCase().replace(',', '.');
  if (str === '') return null;

  const hourMatch = str.match(/^(\d+(?:\.\d+)?)\s*[hu](?:\s*(\d+)\s*m?)?$/);
  if (hourMatch) {
    const hours = parseFloat(hourMatch[1]);
    const minutes = hourMatch[2] ? parseInt(hourMatch[2], 10) : 0;
    return Math.round(hours * 60) + minutes;
  }

  const minuteMatch = str.match(/^(\d+)\s*m$/);
  if (minuteMatch) return parseInt(minuteMatch[1], 10);

  const plainMatch = str.match(/^(\d+)$/);
  if (plainMatch) return parseInt(plainMatch[1], 10);

  return null;
};

/**
 * Calculates the end time given a start time and a duration.
 * @param {string} startTime - The start time in "HH:mm" format.
 * @param {number} duration - The duration in minutes.
 * @returns {string|null} The calculated end time in "HH:mm" format, or null if startTime is invalid.
 */
export const calculateEndTime = (startTime, duration) => {
  if (!startTime) return null;
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = startMinutes + (duration || 0);
  return formatMinutesToTime(endMinutes);
};

/**
 * Calculates the start date and time based on the current moment minus a duration.
 * Handles edge cases like shifting to the previous day automatically.
 *
 * @param {number} durationMinutes - The duration in minutes to subtract from now.
 * @returns {{ date: string, time: string }} An object containing the calculated date (YYYY-MM-DD) and time (HH:mm).
 */
export const calculateStartTimeFromNow = (durationMinutes) => {
  const durationMs = (durationMinutes || 0) * MS_PER_MINUTE;
  return formatDateAndTime(new Date(Date.now() - durationMs));
};
